import { COLORS } from '../../../configs/colors';
import { IntensityStep } from '../../../utils/service-activity-matrix';

/**
 * Sticky first column, for horizontal scrolling on narrow windows.
 *
 * The table has **no vertical scroll of its own** — it runs the full length of
 * its content and the page scrolls. That rules out a sticky header row: the
 * container needs `overflow-x` for the 900px minimum width, and an
 * `overflow-x: auto` element is a scroll container on both axes, so
 * `position: sticky; top: 0` would anchor to a container that never scrolls
 * vertically and simply never stick. The column labels scroll away with the
 * rest of the page instead.
 *
 * `border-collapse: separate` is still mandatory: a sticky cell drops its
 * border under `collapse` (the same bug `configs/tableStyles.ts` documents for
 * the sticky actions column). With `separate`, every border is painted per-cell.
 */
export const STICKY_MATRIX_SX = {
  borderCollapse: 'separate' as const,
  borderSpacing: 0,
  tableLayout: 'fixed' as const,
  width: '100%',
  minWidth: 900,
};

export const LABEL_COLUMN_WIDTH = 210;
export const STATUS_COLUMN_WIDTH = 74;

const CELL_BORDER = `1px solid ${COLORS.CardBorder}`;

export const HEADER_CELL_SX = {
  bgcolor: 'v4.tableEven',
  borderBottom: `1px solid ${COLORS.SubtleGrey}`,
  borderRight: CELL_BORDER,
  p: 0,
  height: 40,
};

/** The corner cell stays pinned horizontally, above the sticky label column. */
export const CORNER_CELL_SX = {
  ...HEADER_CELL_SX,
  position: 'sticky' as const,
  left: 0,
  zIndex: 3,
  width: LABEL_COLUMN_WIDTH,
  px: '10px',
  fontSize: '9px',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  color: 'text.secondary',
  textAlign: 'left' as const,
};

export const STICKY_LABEL_CELL_SX = {
  position: 'sticky' as const,
  left: 0,
  zIndex: 1,
  width: LABEL_COLUMN_WIDTH,
  borderBottom: CELL_BORDER,
  borderRight: CELL_BORDER,
  p: 0,
};

export const NUMERIC_CELL_SX = {
  borderBottom: CELL_BORDER,
  borderRight: CELL_BORDER,
  p: 0,
  textAlign: 'center' as const,
  fontVariantNumeric: 'tabular-nums' as const,
};

/** Full-size button filling a cell, so the whole cell is the hit target. */
export const CELL_BUTTON_SX = {
  display: 'block',
  width: '100%',
  height: '100%',
  minHeight: 30,
  border: 'none',
  bgcolor: 'transparent',
  font: 'inherit',
  fontSize: '13px',
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums' as const,
  color: COLORS.ActivityHeatText,
  cursor: 'pointer',
  px: '4px',
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: '-2px',
  },
};

const COOL_RAMP = [
  'transparent',
  COLORS.ActivityHeatCool1,
  COLORS.ActivityHeatCool2,
  COLORS.ActivityHeatCool3,
  COLORS.ActivityHeatCool4,
];

const WARM_RAMP = [
  'transparent',
  COLORS.ActivityHeatWarm1,
  COLORS.ActivityHeatWarm2,
  COLORS.ActivityHeatWarm3,
  COLORS.ActivityHeatWarm4,
];

/**
 * Background for a cell at a given intensity step.
 *
 * Alert columns (New, Parts Required, Escalated) take the warm ramp — the same
 * "someone must act" semantic the old blanket pink carried, except it now
 * scales with the count instead of firing on any non-zero value.
 */
export const getHeatColor = (step: IntensityStep, isAlert: boolean): string =>
  (isAlert ? WARM_RAMP : COOL_RAMP)[step];
